import React from 'react';
import { VertebraType, EdgeType, Annotations, ImageFile, AnnotationMode, ViewAngleType, OBBAnnotations } from '../../types';
import './ControlPanel.css';

interface ControlPanelProps {
  // Common props
  currentImageFile: ImageFile | null;
  hasOriginalLines: boolean;
  showOriginalLines: boolean;
  originalLinesOpacity: number;
  onShowOriginalLinesToggle: () => void;
  onOpacityChange: (opacity: number) => void;
  onUndo: () => void;
  onClearCurrent: () => void;
  onClearAll: () => void;
  onExport: () => void;
  onImport: (file: File) => void;

  // Triangle mode props
  currentVertebra: VertebraType;
  currentEdge: EdgeType;
  currentTriangleLength: number;
  annotations: Annotations;
  triangleOpacity: number;
  onVertebraChange: (vertebra: VertebraType) => void;
  onEdgeChange: (edge: EdgeType) => void;
  onDeleteCompleted: () => void;
  onTriangleOpacityChange: (opacity: number) => void;

  // OBB mode props
  annotationMode: AnnotationMode;
  currentViewAngle: ViewAngleType;
  currentQuadrilateralLength: number;
  obbAnnotations: OBBAnnotations;
  quadrilateralOpacity: number;
  processedVertebrae: string[];
  onModeChange: (mode: AnnotationMode) => void;
  onViewAngleChange: (viewAngle: ViewAngleType) => void;
  onQuadrilateralOpacityChange: (opacity: number) => void;
  onDeleteOBBAnnotation: (vertebraId: string) => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  // Common props
  currentImageFile,
  hasOriginalLines,
  showOriginalLines,
  originalLinesOpacity,
  onShowOriginalLinesToggle,
  onOpacityChange,
  onUndo,
  onClearCurrent,
  onClearAll,
  onExport,
  onImport,

  // Triangle mode props
  currentVertebra,
  currentEdge,
  currentTriangleLength,
  annotations,
  triangleOpacity,
  onVertebraChange,
  onEdgeChange,
  onDeleteCompleted,
  onTriangleOpacityChange,

  // OBB mode props
  annotationMode,
  currentViewAngle,
  currentQuadrilateralLength,
  obbAnnotations,
  quadrilateralOpacity,
  processedVertebrae,
  onModeChange,
  onViewAngleChange,
  onQuadrilateralOpacityChange,
  onDeleteOBBAnnotation
}) => {
  const importInputRef = React.useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImport(file);
    }
  };

  const getAnnotationCount = (): number => {
    if (annotationMode === 'triangle') {
      let count = 0;
      Object.values(annotations).forEach(vertebraData => {
        if (vertebraData?.upper) count++;
        if (vertebraData?.lower) count++;
      });
      return count;
    } else {
      return Object.keys(obbAnnotations).length;
    }
  };

  const hasCurrentCompleted = (): boolean => {
    if (annotationMode === 'triangle') {
      return !!annotations[currentVertebra]?.[currentEdge];
    } else {
      return false; // OBB mode doesn't have concept of current completed
    }
  };

  const getClassificationProgress = () => {
    const total = Object.keys(obbAnnotations).length;
    const classified = total; // 所有椎體都算已分類，因為預設就是正視
    return { classified, total };
  };

  const renderAnnotationList = () => {
    const items: React.ReactNode[] = [];
    
    if (annotationMode === 'triangle') {
      const vertebrae: VertebraType[] = ['L1', 'L2', 'L3', 'L4', 'L5'];
      
      vertebrae.forEach(vertebra => {
        const vertebraData = annotations[vertebra];
        if (vertebraData) {
          if (vertebraData.upper) {
            items.push(
              <div key={`${vertebra}-upper`} className="annotation-item">
                <strong style={{ color: getVertebraColor(vertebra) }}>{vertebra}</strong> 
                上緣 <small>(3點)</small>
              </div>
            );
          }
          if (vertebraData.lower) {
            items.push(
              <div key={`${vertebra}-lower`} className="annotation-item">
                <strong style={{ color: getVertebraColor(vertebra) }}>{vertebra}</strong> 
                下緣 <small>(3點)</small>
              </div>
            );
          }
        }
      });
    } else {
      Object.entries(obbAnnotations).forEach(([vertebraId, annotation]) => {
        items.push(
          <div key={vertebraId} className="annotation-item">
            <div className="annotation-content">
              <strong style={{ color: getVertebraColor(vertebraId.replace(/[ab]$/i, '') as VertebraType) }}>
                {vertebraId}
              </strong> 
              {annotation.viewAngle} <small>(4點)</small>
            </div>
            <button 
              className="btn btn-sm btn-danger delete-annotation-btn"
              onClick={() => onDeleteOBBAnnotation(vertebraId)}
              title="刪除此標記"
            >
              🗑️
            </button>
          </div>
        );
      });
    }

    return items.length > 0 ? items : (
      <div className="no-annotations">尚無標記資料</div>
    );
  };

  const getVertebraColor = (vertebra: VertebraType): string => {
    const colors = {
      L1: '#FF6B6B',
      L2: '#4ECDC4',
      L3: '#45B7B8',
      L4: '#96CEB4',
      L5: '#FFEAA7'
    };
    return colors[vertebra];
  };

  return (
    <div className="control-panel">
      {/* Mode Selection */}
      <div className="mode-selector">
        <h4>標記模式</h4>
        <div className="mode-buttons">
          <button 
            className={`btn ${annotationMode === 'triangle' ? 'btn-primary active' : 'btn-secondary'}`}
            onClick={() => onModeChange('triangle')}
          >
            🔺 三角形模式
          </button>
          <button 
            className={`btn ${annotationMode === 'obb' ? 'btn-primary active' : 'btn-secondary'}`}
            onClick={() => onModeChange('obb')}
          >
            🔶 視角分類模式
          </button>
        </div>
      </div>

      {currentImageFile && (
        <div className="current-image-info">
          <h4>當前影像</h4>
          <p className="image-name">{currentImageFile.name}</p>
          {currentImageFile.csvPath && (
            <p className="csv-info">✓ 已載入原始標記</p>
          )}
        </div>
      )}

      {annotationMode === 'triangle' ? (
        // Triangle Mode Controls
        <>
          <div className="vertebra-selector">
            <label>選擇椎體：</label>
            <select 
              value={currentVertebra} 
              onChange={(e) => onVertebraChange(e.target.value as VertebraType)}
            >
              <option value="L1">L1 (第1腰椎)</option>
              <option value="L2">L2 (第2腰椎)</option>
              <option value="L3">L3 (第3腰椎)</option>
              <option value="L4">L4 (第4腰椎)</option>
              <option value="L5">L5 (第5腰椎)</option>
            </select>
          </div>

          <div className="edge-selector">
            <button 
              className={currentEdge === 'upper' ? 'active' : ''}
              onClick={() => onEdgeChange('upper')}
            >
              上緣
            </button>
            <button 
              className={currentEdge === 'lower' ? 'active' : ''}
              onClick={() => onEdgeChange('lower')}
            >
              下緣
            </button>
          </div>

          <div className="point-counter">
            <div>當前三角形：{currentTriangleLength}/3 點</div>
            <div className="current-label">
              {currentVertebra} {currentEdge === 'upper' ? '上緣' : '下緣'}
            </div>
          </div>

          <div className="triangle-controls">
            <h4>三角形標記控制</h4>
            <div className="opacity-control">
              <label>透明度：</label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={triangleOpacity}
                onChange={(e) => onTriangleOpacityChange(parseFloat(e.target.value))}
              />
              <span className="opacity-value">{Math.round(triangleOpacity * 100)}%</span>
            </div>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-secondary" 
              onClick={onUndo}
              disabled={currentTriangleLength === 0}
            >
              ↶ 撤銷上一點
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={onClearCurrent}
              disabled={currentTriangleLength === 0}
            >
              清除當前三角形
            </button>
            <button 
              className="btn btn-warning" 
              onClick={onDeleteCompleted}
              disabled={!hasCurrentCompleted()}
            >
              🗑️ 刪除已完成標記
            </button>
            <button className="btn btn-danger" onClick={onClearAll}>
              清除全部標記
            </button>
          </div>
        </>
      ) : (
        // OBB Mode Controls
        <>
          <div className="view-angle-selector">
            <h4>選擇視角</h4>
            <div className="view-angle-buttons">
              <button 
                className={currentViewAngle === '仰視' ? 'active' : ''}
                onClick={() => onViewAngleChange('仰視')}
              >
                仰視
              </button>
              <button 
                className={currentViewAngle === '正視' ? 'active' : ''}
                onClick={() => onViewAngleChange('正視')}
              >
                正視
              </button>
              <button 
                className={currentViewAngle === '俯視' ? 'active' : ''}
                onClick={() => onViewAngleChange('俯視')}
              >
                俯視
              </button>
            </div>
          </div>

          <div className="vertebra-status">
            <h4>分類進度</h4>
            <div className="vertebra-progress">
              已分類：{getClassificationProgress().classified} / {getClassificationProgress().total}
            </div>
            <div className="current-angle">
              當前視角：<strong>{currentViewAngle}</strong>
            </div>
          </div>

          <div className="instructions">
            <h4>操作說明</h4>
            <div><strong>仰視</strong>：從下往上看椎體的角度，標籤顯示橙色背景</div>
            <div><strong>正視</strong>：從正面看椎體的角度，標籤顯示綠色背景</div>
            <div><strong>俯視</strong>：從上往下看椎體的角度，標籤顯示藍色背景</div>
            <div style={{ marginTop: '8px', fontSize: '0.9rem' }}>
              操作：先選擇視角按鈕，再點擊椎體區域進行分類
            </div>
          </div>

          <div className="quadrilateral-controls">
            <h4>四邊形標記控制</h4>
            <div className="opacity-control">
              <label>透明度：</label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={quadrilateralOpacity}
                onChange={(e) => onQuadrilateralOpacityChange(parseFloat(e.target.value))}
              />
              <span className="opacity-value">{Math.round(quadrilateralOpacity * 100)}%</span>
            </div>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-warning" 
              onClick={() => {
                // Reset all to default
                const resetAnnotations = { ...obbAnnotations };
                Object.keys(resetAnnotations).forEach(vertebraId => {
                  resetAnnotations[vertebraId] = {
                    ...resetAnnotations[vertebraId],
                    viewAngle: '正視'
                  };
                });
                // Use the parent's callback through a custom prop or direct state update
                onClearAll(); // This will reset all annotations
              }}
            >
              重置所有分類
            </button>
          </div>
        </>
      )}

      {hasOriginalLines && (
        <div className="original-lines-controls">
          <h4>原始線段控制</h4>
          <label className="toggle-container">
            <input
              type="checkbox"
              checked={showOriginalLines}
              onChange={onShowOriginalLinesToggle}
            />
            <span className="toggle-label">顯示原始線段</span>
          </label>
          
          {showOriginalLines && (
            <div className="opacity-control">
              <label>透明度：</label>
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={originalLinesOpacity}
                onChange={(e) => onOpacityChange(parseFloat(e.target.value))}
              />
              <span className="opacity-value">{Math.round(originalLinesOpacity * 100)}%</span>
            </div>
          )}
        </div>
      )}

      <div className="annotation-list">
        <h4>{annotationMode === 'triangle' ? '三角形' : 'OBB'} 標記列表 ({getAnnotationCount()})</h4>
        <div className="list-container">
          {renderAnnotationList()}
        </div>
      </div>

      <div className="export-section">
        <button className="btn btn-primary" onClick={onExport}>
          📤 匯出{annotationMode === 'triangle' ? '三角形' : 'OBB'}CSV
        </button>
        {annotationMode === 'triangle' && (
          <button className="btn btn-secondary" onClick={handleImportClick}>
            📥 匯入三角形CSV
          </button>
        )}
        <input
          ref={importInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleImportChange}
        />
      </div>
    </div>
  );
};